import { isWeekend } from "date-fns";
import { call, select } from "redux-saga/effects";

import LeaderboardMetricFieldName from "@mapmycustomers/shared/enum/fieldModel/LeaderboardMetricFieldName";
import { AnyEntity } from "@mapmycustomers/shared/types/entity";
import LeaderboardItem from "@mapmycustomers/shared/types/entity/LeaderboardItem";
import Organization from "@mapmycustomers/shared/types/Organization";
import ListResponse from "@mapmycustomers/shared/types/viewModel/ListResponse";

import { getDatesRange } from "@app/enum/dashboard/dateRanges";
import NewRoutesCardData from "@app/scene/dashboard/store/cards/newRoutes/NewRoutesCardData";
import { getFilters } from "@app/scene/dashboard/store/cards/newRoutes/sagas";
import { callApi } from "@app/store/api/callApi";
import { getOrganizationId } from "@app/store/iam";
import NewRoutesCardConfiguration from "@app/types/dashboard/cards/newRoutes/NewRoutesCardConfiguration";
import Scope from "@app/types/dashboard/Scope";
import AggregatedListResponse from "@app/types/viewModel/AggregatedListResponse";
import { doesWeekStartFromMonday, getLocalTimeZone } from "@app/util/dates";
import { parseApiDate } from "@app/util/parsers";

import { DateAggregationItem, granularityByDateRange } from "./common";

export function* fetchNewRoutesCardDataHelper(params: {
  callback: (data: NewRoutesCardData) => void;
  configuration: NewRoutesCardConfiguration;
  scope?: Scope;
}) {
  const { callback, configuration, scope } = params;
  const { dateRange, preferences } = configuration;
  const isTeamScope = !!scope?.teamId && !scope?.userId;

  const orgId: Organization["id"] = yield select(getOrganizationId);

  const granularity = granularityByDateRange[dateRange.range];

  const filters = getFilters(scope, configuration);

  const payload = {
    $aggs: preferences.showChart
      ? {
          count: ["id"],
          primaryGroup: {
            field: "createdAt",
            interval: granularity,
            offset: granularity !== "week" || doesWeekStartFromMonday() ? undefined : "-1 day",
            timezone: getLocalTimeZone(),
          },
        }
      : undefined,
    $filters: filters,
    $limit: 0,
  };

  const response: AggregatedListResponse<AnyEntity, DateAggregationItem[] | undefined> =
    yield callApi("fetchUnifiedRoutesAggregated", orgId, payload);

  let sum = 0;
  const result: NewRoutesCardData = {
    count: response.total,
    history: response.aggregations?.map((item) => {
      sum += item.doc_count;
      return {
        count: preferences.cumulative ? sum : item.doc_count,
        date: parseApiDate(item.key),
      };
    }),
  };

  if (preferences.showTrend) {
    // fetch again, but for the previous range
    const previousRange = getDatesRange(dateRange.range, dateRange.subRange, true);
    const previousPayload = {
      $aggs: preferences.showChart
        ? {
            count: ["id"],
            primaryGroup: {
              field: "createdAt",
              interval: granularity,
              offset: granularity !== "week" || doesWeekStartFromMonday() ? undefined : "-1 day",
              timezone: getLocalTimeZone(),
            },
          }
        : undefined,
      $filters: {
        ...filters,
        createdAt: {
          $gte: previousRange.startDate.toISOString(),
          $lte: previousRange.endDate.toISOString(),
        },
      },
      $limit: 0,
    };

    const previousResponse: AggregatedListResponse<AnyEntity, DateAggregationItem[] | undefined> =
      yield callApi("fetchUnifiedRoutesAggregated", orgId, previousPayload);
    result.previousCount = previousResponse.total;
    if (preferences.showChart) {
      result.previousHistory = previousResponse.aggregations?.map((item) => ({
        count: item.doc_count,
        date: parseApiDate(item.key),
      }));
    }
  }

  const filteredHistory = result.history?.filter(
    ({ count, date }, index) =>
      !isWeekend(date) || count || (result.previousHistory && result.previousHistory[index]?.count)
  );

  const filteredPreviousHistory = result.previousHistory?.filter(
    ({ count }, index) =>
      count ||
      (result.history && (result.history[index]?.count || !isWeekend(result.history[index]?.date)))
  );

  result.history = filteredHistory;
  result.previousHistory = filteredPreviousHistory;
  // if we should show the rank and scope is not the Entire Org
  if (preferences.showRankPosition && (scope?.teamId || scope?.userId)) {
    const response: ListResponse<LeaderboardItem> = yield callApi(
      "fetchLeaderboardData",
      orgId,
      {
        date: filters.createdAt,
        includePerformanceMetric: true,
        property: { $in: [LeaderboardMetricFieldName.ROUTES_CREATED] },
        summary: isTeamScope ? "team" : "user",
      },
      undefined
    );
    const rank = isTeamScope
      ? response.data.findIndex(({ teamId }) => teamId === scope!.teamId)
      : response.data.findIndex(({ userId }) => userId === scope!.userId);
    result.rank = rank >= 0 ? rank + 1 : undefined;
  }

  yield call(callback, result);
}
